/**
 * Delivery-scoped invitation deep-link navigation claim.
 *
 * Distinguishes accidental duplicate routing of ONE native delivery from a later
 * intentional open of the same invite code.
 *
 * Semantics:
 * - cold_start: for a given launch code, admit at most once per JS process so
 *   CapApp.getLaunchUrl() re-reads / effect re-runs cannot reinject after leave.
 *   A different cold-start code is still admittable.
 * - warm_open: each genuine appUrlOpen is a new delivery and is always admitted
 *   for a valid code (including the same code after the user closed a prior dialog).
 *
 * Never session-dismisses invite codes (no markInviteDismissed).
 * Never logs raw invite codes.
 */

export type InviteDeepLinkSource = 'cold_start' | 'warm_open';

/**
 * Codes already admitted from cold_start getLaunchUrl bootstrap in this process.
 * Scoped to cold-start duplicates only — does not gate warm opens.
 */
const coldStartAdmittedCodes = new Set<string>();

/**
 * Atomically decide whether to admit React-Router navigation to `/invite/:code`.
 *
 * @returns true when this delivery should navigate.
 */
export function claimInviteDeepLinkNavigation(
  inviteCode: string,
  source: InviteDeepLinkSource
): boolean {
  if (!inviteCode || typeof inviteCode !== 'string') {
    return false;
  }

  if (source === 'cold_start') {
    if (coldStartAdmittedCodes.has(inviteCode)) {
      return false;
    }
    coldStartAdmittedCodes.add(inviteCode);
    return true;
  }

  // warm_open: intentional taps always process (even after prior exit of same code).
  return true;
}

/**
 * Exit the active invite presentation (Close / Go to Network).
 * Does not permanently block the invite code. Cold-start membership is intentionally
 * retained so the same launch URL cannot reinject after the user leaves.
 */
export function releaseInviteRouteDelivery(_inviteCode?: string): void {
  // Intentionally does not clear cold-start admits or markInviteDismissed.
}

/** Test isolation — not for production callers. */
export function __resetInviteDeepLinkClaimsForTests(): void {
  coldStartAdmittedCodes.clear();
}
